import * as React from 'react';
import { usePathPattern } from '@/components/utils/usePathPattern';

import './page-layout.scss';
import useMaintenanceGateway from '@/modules/maintenance/MaintenanceGateway';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import transferPropertiesToComponent from '@/page-components/ComponentData/transferPropertiesToComponent';
import { useAppSelector } from '@/store';
import PageLoader from '../page-loader';
import craftJsParser from '../utils/craftJsParser';
import { PageDataContext } from '../utils/PageDataProvider';

type PageLayoutProps = {
  id: string;
  pageId?: string | undefined;
};

const PageLayout: React.FC<PageLayoutProps> = (props) => {
  const { id, pageId } = props;

  const pattern = usePathPattern();
  const config = useAppSelector((state) => state.templatesConfig);
  const authenticated = useAppSelector((state) => state.authentication && state.authentication.auth_type === 'user');
  const isAuthInProgress = useAppSelector((state) => state.authentication.in_progress);
  const pageLayoutsOverrides = useAppSelector((state) => state.templatesConfig.pageLayoutsOverrides);
  const pageDataContext = React.useContext(PageDataContext);

  const action = useMaintenanceGateway();

  const pageLayout = config.pageLayouts[id];
  let projectData: any = pageLayout.projectData;

  const page = pageId ? config.pages[pageId] : null;

  if (page && page.a && !authenticated && config.setPages && config.setPages['/login']) {
    if (config.setPages['/login'].page_layout_id) {
      projectData = config.pageLayouts[config.setPages['/login'].page_layout_id].projectData;
    } else {
      projectData = config.pages[config.setPages['/login'].page_id].projectData;
    }
  }

  const data: any = React.useMemo(() => {
    if (!id || !projectData) return null;

    const rootNodeId = 'ROOT';
    const data = JSON.parse(projectData);

    const omittedKeys: Record<string, boolean> = { dsId: true, deId: true, dsType: true, expose: true };

    const rootProps: any = {};

    if (action?.redirect && config?.setPages?.['/maintenance/default']?.page_layout_id) {
      const pageLayoutId = config.setPages['/maintenance/default'].page_layout_id;
      const pageId = config.setPages['/maintenance/default'].page_id;
      const overrideId = `${pattern}|${pageId}|${pageLayoutId}`;
      const overrides = pageLayoutsOverrides[overrideId];

      if (overrides && Object.keys(overrides).length) {
        transferPropertiesToComponent(data, overrides, true, false, true);
      }

      Object.keys(data[rootNodeId].props.properties).forEach((key) => {
        if (typeof key !== 'string') key = key.toString();

        if (!omittedKeys[key]) {
          rootProps[key] = data[rootNodeId].props.properties[key];
        }
      });

      const content = craftJsParser({
        craftState: data,
        rootNodeId,
        pageLayoutId,
        pageId,
        pageType: pageLayoutId ? 'layout' : 'page',
        options: {
          authenticated,
          overrideId,
        },
        withProvider: true,
      });

      return {
        content: <div className="page-layout">{content}</div>,
        isPage: false,
        contextValue: {
          ...rootProps,
          parentContext: pageDataContext,
          __id: pageLayoutId,
        },
      };
    } else if (page && page.a && !authenticated) {
      if (isAuthInProgress) {
        return { content: <PageLoader />, isPage: false, contextValue: {} };
      }

      if (config.setPages && config.setPages['/login']) {
        const pageLayoutId = config.setPages['/login'].page_layout_id;
        const pageId = config.setPages['/login'].page_id;
        const overrideId = `${pattern}|${pageId}|${pageLayoutId}`;
        const overrides = pageLayoutsOverrides[overrideId];

        if (overrides && Object.keys(overrides).length) {
          transferPropertiesToComponent(data, overrides, true, false, true);
        }

        Object.keys(data[rootNodeId].props.properties).forEach((key) => {
          if (typeof key !== 'string') key = key.toString();

          if (!omittedKeys[key]) {
            rootProps[key] = data[rootNodeId].props.properties[key];
          }
        });

        const content = craftJsParser({
          craftState: data,
          rootNodeId,
          pageLayoutId,
          pageId,
          pageType: pageLayoutId ? 'layout' : 'page',
          options: {
            authenticated,
            overrideId,
          },
          withProvider: true,
        });

        return {
          content: <div className="page-layout">{content}</div>,
          isPage: false,
          contextValue: {
            ...rootProps,
            parentContext: pageDataContext,
            __id: pageLayoutId,
          },
        };
      }
    }

    const overrideId = `${pattern}|${pageId}|${id}`;
    const overrides = pageLayoutsOverrides[overrideId] ?? {};

    if (overrides && Object.keys(overrides).length) {
      transferPropertiesToComponent(data, overrides, true, false, true);
    }

    Object.keys(data[rootNodeId].props.properties).forEach((key) => {
      if (typeof key !== 'string') key = key.toString();

      if (!omittedKeys[key]) {
        rootProps[key] = data[rootNodeId].props.properties[key];
      }
    });

    //const rootKey = sha1(JSON.stringify(overrides, Object.keys(overrides).sort()), 'hex');
    //console.log('PageLayout overrides', { rootKey, pageLayout: id, page: overrideId, overrides });

    const content = craftJsParser({
      craftState: data,
      rootNodeId,
      pageLayoutId: id,
      //rootKey,
      pageId,
      pageType: 'layout',
      options: {
        authenticated,
        overrideId,
      },
      withProvider: true,
    });

    return {
      content,
      isPage: true,
      contextValue: {
        ...rootProps,
        parentContext: pageDataContext,
        __id: id,
      },
    };
  }, [
    id,
    pageId,
    action,
    config,
    projectData,
    page,
    authenticated,
    isAuthInProgress,
    pageLayoutsOverrides,
    pattern,
    pageDataContext,
  ]);

  return (
    <DataElementContext.Provider value={data.contextValue}>
      {data.isPage ? <div className="page-layout">{data.content}</div> : data.content}
    </DataElementContext.Provider>
  );
};

export default PageLayout;
